import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/features/platform/contexts/auth-context';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowRight, Mail, Building2, AppWindow } from 'lucide-react';
import { initiateLogin, getTenantSlugFromUrl } from '@/backend/api/auth/authorize';
import { http } from '@/libs/http';

interface ApplicationInfo {
    id: string;
    name: string;
    base_url?: string;
    logo_url?: string;
}

interface TenantSummary {
    id: string;
    slug: string;
    name: string;
    logo_url?: string;
}

export const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const { user, isLoading, login } = useAuth();
    const [searchParams] = useSearchParams();
    const [isRedirecting, setIsRedirecting] = useState(false);
    const hasHandledToken = useRef(false);

    // Auth flow state
    const [emailOrDomain, setEmailOrDomain] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [candidateTenants, setCandidateTenants] = useState<TenantSummary[]>([]);
    const [hasPlatformTenant, setHasPlatformTenant] = useState(false);

    // Application context (when redirected from an external app)
    const [applicationInfo, setApplicationInfo] = useState<ApplicationInfo | null>(null);

    type ScreenMode = 'initial' | 'discover' | 'select';
    const [mode, setMode] = useState<ScreenMode>('initial');
    const [tenantSlug, setTenantSlug] = useState<string>('');

    // Fetch application info from `application` URL param if present
    useEffect(() => {
        const appParam = searchParams.get('application');
        if (!appParam) return;

        // If it looks like a plain URL (not an id/name), skip lookup
        const isUrl = appParam.startsWith('http://') || appParam.startsWith('https://') || appParam.startsWith('/');
        if (isUrl) return;

        http.get<ApplicationInfo>(`/auth/lookups/applications/${encodeURIComponent(appParam)}`)
            .then(setApplicationInfo)
            .catch(() => {
                // Silently fail — the application param might just be a return URL
            });
    }, [searchParams]);

    const getValidatedRedirectUrl = (
        rawReturnUrl: string | null,
        isPlatformLogin: boolean,
        currentUser: typeof user,
        tenantParamSlug: string | null
    ): string => {
        const isPlatformUser = isPlatformLogin || Boolean(currentUser?.admin || currentUser?.roles?.some(r => r.scope === 'platform'));
        const defaultTenantSlug = tenantParamSlug || currentUser?.tenant_slug || '';
        const defaultPath = isPlatformUser ? '/m' : (defaultTenantSlug ? `/t/${defaultTenantSlug}` : '/m');

        if (!rawReturnUrl) return defaultPath;

        // Ignore login or auth error URLs
        if (rawReturnUrl.includes('/auth/login') || rawReturnUrl.includes('/login') || rawReturnUrl === '/') {
            return defaultPath;
        }

        // 1) Management paths (/m or /m/...)
        if (rawReturnUrl.startsWith('/m')) {
            // If user logged in using tenant login instead of platform login, redirect to respective default page
            if (!isPlatformUser) {
                return defaultTenantSlug ? `/t/${defaultTenantSlug}` : '/m';
            }
            return rawReturnUrl;
        }

        // 2) Tenant paths (/t or /t/...)
        if (rawReturnUrl.startsWith('/t')) {
            // If platform login was used, redirect to platform default page (/m)
            if (isPlatformLogin) {
                return '/m';
            }

            // Extract tenant slug from returnUrl (e.g. /t/acme/projects -> acme)
            const match = rawReturnUrl.match(/^\/t\/([^\/]+)/);
            const returnUrlTenantSlug = match ? match[1] : null;

            // If returnUrl is for a different tenant than the logged-in tenant, redirect to logged-in tenant default page
            if (returnUrlTenantSlug && defaultTenantSlug && returnUrlTenantSlug !== defaultTenantSlug) {
                return `/t/${defaultTenantSlug}`;
            }
            return rawReturnUrl;
        }

        return rawReturnUrl;
    };

    useEffect(() => {
        const handleToken = async () => {
            if (hasHandledToken.current) return true;

            const accessToken = searchParams.get('access_token');
            const tenantParam = searchParams.get('tenant');
            const platformLogin = searchParams.get('platform_login') === 'true';

            if (accessToken) {
                hasHandledToken.current = true;
                setIsRedirecting(true);
                try {
                    let finalToken = accessToken;
                    const tenant = platformLogin ? 'platform' : (tenantParam || 'platform');
                    const clientId = searchParams.get('client_id') || searchParams.get('application') || (platformLogin ? 'platform' : '');

                    try {
                        const tokenResponse = await http.post<{ access_token: string; user_status?: number }>(
                            '/auth/tokens',
                            { scope: clientId || undefined },
                            {
                                headers: {
                                    Authorization: `Bearer ${accessToken}`,
                                    'x-tenant': tenant,
                                    'x-client-id': clientId || undefined,
                                }
                            }
                        );
                        if (tokenResponse && tokenResponse.access_token) {
                            finalToken = tokenResponse.access_token;
                        }
                        // If user is pending (status 0), redirect to waiting room
                        if (!platformLogin && tokenResponse?.user_status === 0) {
                            await login(finalToken, accessToken);
                            navigate(`/t/${tenantParam || tenant}/waiting-room`);
                            return true;
                        }
                    } catch (tokenErr: any) {
                        console.error("Token exchange failed:", tokenErr);
                        setError('Failed to exchange login token. Unauthorized.');
                        setIsRedirecting(false);
                        return true;
                    }

                    const loggedInUser = await login(finalToken, accessToken);

                    // Redirect back to original page or default page based on URL format and login context
                    const returnUrlParam = searchParams.get('returnUrl');
                    const rawReturnUrl = returnUrlParam || sessionStorage.getItem('auth_return_url');
                    try {
                        sessionStorage.removeItem('auth_return_url');
                    } catch (e) { }

                    const targetUrl = getValidatedRedirectUrl(
                        rawReturnUrl,
                        platformLogin,
                        loggedInUser,
                        tenantParam || loggedInUser?.tenant_slug || null
                    );
                    navigate(targetUrl);
                } catch (err) {
                    setError('Failed to log in with provided token.');
                    setIsRedirecting(false);
                }
                return true;
            }
            return false;
        };

        handleToken().then((handled) => {
            if (handled) return;

            // Preserve returnUrl in sessionStorage if present in query params
            const returnUrlParam = searchParams.get('returnUrl');
            if (returnUrlParam) {
                try {
                    sessionStorage.setItem('auth_return_url', returnUrlParam);
                } catch (e) { }
            }

            // If already authenticated and no token in URL, redirect
            if (!isLoading && user) {
                const rawReturnUrl = returnUrlParam || sessionStorage.getItem('auth_return_url');
                try {
                    sessionStorage.removeItem('auth_return_url');
                } catch (e) { }

                const isPlatformLogin = searchParams.get('platform_login') === 'true';
                const targetUrl = getValidatedRedirectUrl(
                    rawReturnUrl,
                    isPlatformLogin,
                    user,
                    searchParams.get('tenant') || user.tenant_slug || null
                );
                navigate(targetUrl);
                return;
            }

            // Auto-detect tenant slug from URL
            const detectedSlug = getTenantSlugFromUrl() || searchParams.get('tenant');
            if (detectedSlug) {
                setTenantSlug(detectedSlug);
                setMode('initial');
            } else {
                setMode('discover');
            }
        });
    }, [user, isLoading, navigate, searchParams, login]);

    const handleLoginWithSlug = (slugToUse: string) => {
        setIsRedirecting(true);
        const appParam = searchParams.get('application') || searchParams.get('app');
        const returnUrl = searchParams.get('returnUrl') || sessionStorage.getItem('auth_return_url');
        if (returnUrl) {
            try {
                sessionStorage.setItem('auth_return_url', returnUrl);
            } catch (e) { }
        }
        initiateLogin(slugToUse, appParam || undefined, returnUrl || undefined);
    };

    const handleDiscover = async () => {
        const query = emailOrDomain.trim().toLowerCase();
        if (!query) {
            setError('Enter your email or domain to continue.');
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            const tenants = await http.get<TenantSummary[]>(
                `/auth/lookups/tenants/${encodeURIComponent(query)}`
            );

            if (!tenants || tenants.length === 0) {
                setCandidateTenants([]);
                setHasPlatformTenant(false);
                setError('No account found. Contact your administrator.');
                return;
            }

            const platformTenantId = '00000000-0000-0000-0000-000000000000';
            const platformTenant = tenants.find(t => t.id === platformTenantId);
            const customerTenants = tenants.filter(t => t.id !== platformTenantId);

            const hasAppParam = Boolean(searchParams.get('application'));
            const allowPlatform = !hasAppParam && !!platformTenant;

            setHasPlatformTenant(allowPlatform);
            setCandidateTenants(customerTenants);
            setMode('select');
        } catch {
            setError('Unable to search for your organization right now.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-white dark:bg-neutral-950">
                <div className="w-8 h-8 rounded-full border-2 border-slate-200 dark:border-neutral-800 border-t-blue-500 animate-spin" />
            </div>
        );
    }

    return (
        <div className="flex min-h-screen w-full bg-slate-50 dark:bg-neutral-950 text-slate-900 dark:text-white selection:bg-blue-500/30 font-sans transition-colors duration-500">
            {/* Ambient Background Glow */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-slate-200/40 dark:bg-slate-800/20 blur-[120px]" />
                <div className="absolute top-[40%] right-[-10%] w-[40%] h-[40%] rounded-full bg-slate-200/30 dark:bg-slate-800/15 blur-[100px]" />
                <div className="absolute bottom-[-20%] left-[20%] w-[60%] h-[60%] rounded-full bg-slate-100/40 dark:bg-slate-900/20 blur-[120px]" />
            </div>

            <div className="relative z-10 flex flex-col items-center justify-center w-full px-4 sm:px-6">

                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                    className="w-full max-w-md"
                >
                    {/* Glassmorphism Log in Card */}
                    <div className="backdrop-blur-xl bg-white/70 dark:bg-white/3 border border-slate-200 dark:border-white/8 rounded-3xl p-8 sm:p-10 shadow-[0_8px_32px_0_rgba(0,0,0,0.05)] dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.3)] relative overflow-hidden group">

                        {/* Shimmer overlay on hover */}
                        <div className="absolute inset-0 -translate-x-full bg-linear-to-r from-transparent via-slate-500/5 dark:via-white/4 to-transparent group-hover:animate-shimmer pointer-events-none" />

                        <div className="flex flex-col items-center text-center space-y-8 relative z-10">

                            {/* Logo / Icon */}
                            <motion.div
                                initial={{ scale: 0.8, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{ delay: 0.2, duration: 0.5, ease: "backOut" }}
                                className="flex items-center justify-center py-2 w-full"
                            >
                                <img src="/brand/light-logo-with-text.svg" alt="Argus Logo" className="h-16 sm:h-20 w-auto max-w-[85%] dark:hidden object-contain" />
                                <img src="/brand/dark-logo-with-text.svg" alt="Argus Logo" className="h-16 sm:h-20 w-auto max-w-[85%] hidden dark:block object-contain" />
                            </motion.div>

                            {/* Application context — shown when logging in via an app link */}
                            <AnimatePresence>
                                {applicationInfo && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -4 }}
                                        transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                                        className="w-full text-center"
                                    >
                                        {/* App logo */}
                                        <div className="flex justify-center mb-3">
                                            <div className="w-12 h-12 rounded-xl overflow-hidden flex items-center justify-center bg-slate-100 dark:bg-white/6 border border-slate-200 dark:border-white/8">
                                                {applicationInfo.logo_url ? (
                                                    <img
                                                        src={applicationInfo.logo_url}
                                                        alt={applicationInfo.name}
                                                        className="w-8 h-8 object-contain"
                                                        onError={(e) => {
                                                            (e.target as HTMLImageElement).style.display = 'none';
                                                            const fb = e.currentTarget.parentElement?.querySelector('.logo-fallback');
                                                            if (fb) (fb as HTMLElement).style.display = 'flex';
                                                        }}
                                                    />
                                                ) : null}
                                                <AppWindow className={`logo-fallback w-5 h-5 text-slate-400 ${applicationInfo.logo_url ? 'hidden' : ''}`} />
                                            </div>
                                        </div>
                                        <p className="text-[11px] font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-1">Signing in to</p>
                                        <p className="text-base font-bold text-slate-800 dark:text-white">{applicationInfo.name}</p>
                                        <div className="mt-4 h-px w-full bg-slate-200 dark:bg-white/8" />
                                    </motion.div>
                                )}
                            </AnimatePresence>


                            {/* Title depending on mode */}
                            <div className="space-y-2 pt-2">
                                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">
                                    {/* {mode === 'select' ? 'Select Organization' : 'Welcome to SmartSite'} */}
                                    {mode === 'select' ? null : 'Welcome to SmartSite'}
                                </h1>
                                <p className="text-[15px] text-slate-700 italic dark:text-neutral-400 font-medium text-left">
                                    {mode === 'discover' && 'Enter your email or domain to continue...'}
                                    {mode === 'select' && 'Select Your Organization to login'}
                                    {mode === 'initial' && 'Identity & Access Management'}
                                </p>
                            </div>

                            {/* Content based on mode */}
                            <div className="w-full pt-1 space-y-4">
                                {mode === 'initial' && (
                                    <>
                                        <button
                                            onClick={() => handleLoginWithSlug(tenantSlug)}
                                            disabled={isRedirecting}
                                            className="relative w-full group/btn flex items-center justify-center gap-3 bg-slate-900 dark:bg-white text-white dark:text-neutral-950 px-6 py-4 rounded-xl font-semibold transition-all duration-300 hover:bg-slate-800 dark:hover:bg-neutral-100 hover:scale-[1.02] active:scale-[0.98] cursor-pointer disabled:opacity-70 disabled:hover:scale-100 disabled:cursor-not-allowed overflow-hidden shadow-lg dark:shadow-[0_0_20px_rgba(255,255,255,0.1)]"
                                        >
                                            {isRedirecting ? (
                                                <div className="flex items-center gap-3">
                                                    <div className="w-5 h-5 rounded-full border-2 border-slate-400 dark:border-neutral-300 border-t-white dark:border-t-neutral-950 animate-spin" />
                                                    <span>Redirecting...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <span className="tracking-wide text-sm uppercase">Continue</span>
                                                    <ArrowRight className="w-4.5 h-4.5 opacity-0 -ml-4 group-hover/btn:opacity-100 group-hover/btn:ml-0 transition-all duration-300 ease-out" />
                                                </>
                                            )}
                                        </button>
                                        <div className="text-center">
                                            <button
                                                onClick={() => setMode('discover')}
                                                className="text-xs text-slate-400 dark:text-neutral-600 hover:text-slate-600 dark:hover:text-neutral-400 transition-colors"
                                            >
                                                Use another organization →
                                            </button>
                                        </div>
                                    </>
                                )}

                                {mode === 'discover' && (
                                    <>
                                        <div className="relative text-left w-full group/input">
                                            {/* <label htmlFor="email" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                                                Work email or Domain
                                            </label> */}
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <Mail className="h-5 w-5 text-slate-400" />
                                                </div>
                                                <input
                                                    id="email"
                                                    type="text"
                                                    value={emailOrDomain}
                                                    onChange={(e) => setEmailOrDomain(e.target.value)}
                                                    placeholder="name@company.com or company.com"
                                                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 dark:border-white/10 bg-white/50 dark:bg-slate-900/50 text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all placeholder:text-slate-400"
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') handleDiscover();
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {error && <p className="text-sm text-red-500 text-left">{error}</p>}

                                        <button
                                            onClick={handleDiscover}
                                            disabled={isSubmitting || !emailOrDomain.trim()}
                                            className="relative w-full group/btn flex items-center justify-center gap-3 bg-slate-900 dark:bg-white text-white dark:text-neutral-950 px-6 py-4 rounded-xl font-semibold transition-all duration-300 hover:bg-slate-800 dark:hover:bg-neutral-100 hover:scale-[1.02] active:scale-[0.98] cursor-pointer disabled:opacity-70 disabled:hover:scale-100 disabled:cursor-not-allowed shadow-lg"
                                        >
                                            {isSubmitting ? (
                                                <div className="flex items-center gap-3">
                                                    <div className="w-5 h-5 rounded-full border-2 border-slate-400 dark:border-neutral-300 border-t-white dark:border-t-neutral-950 animate-spin" />
                                                    <span>Searching...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    <span className="tracking-wide text-sm uppercase">Find Organization</span>
                                                    <ArrowRight className="w-4.5 h-4.5 opacity-0 -ml-4 group-hover/btn:opacity-100 group-hover/btn:ml-0 transition-all duration-300 ease-out" />
                                                </>
                                            )}
                                        </button>
                                    </>
                                )}

                                {mode === 'select' && (
                                    <div className="flex flex-col gap-3 w-full">
                                        {candidateTenants.length > 0 ? (
                                            <div className="grid gap-2 max-h-60 overflow-y-auto pr-2 custom-scrollbar text-left">
                                                {candidateTenants.map((t) => (
                                                    <button
                                                        key={t.id}
                                                        onClick={() => handleLoginWithSlug(t.slug)}
                                                        className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white/50 px-4 py-3 text-left transition hover:border-blue-300 hover:bg-blue-50 dark:border-white/10 dark:bg-slate-900/50 dark:hover:border-blue-500/30 dark:hover:bg-blue-500/10 group/item"
                                                    >
                                                        {t.logo_url ? (
                                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-0.5 overflow-hidden shadow-sm">
                                                                <img src={t.logo_url} alt={t.name} className="h-full w-full object-contain rounded" />
                                                            </div>
                                                        ) : (
                                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 group-hover/item:text-blue-500 group-hover/item:bg-blue-100 dark:group-hover/item:bg-blue-900/30 transition-colors">
                                                                <Building2 className="h-5 w-5" />
                                                            </div>
                                                        )}
                                                        <div className="flex-1 overflow-hidden">
                                                            <p className="font-semibold text-sm truncate">{t.name}</p>
                                                            {/* <p className="text-xs text-slate-500 dark:text-slate-400 truncate">/{t.slug}</p> */}
                                                        </div>
                                                        <ArrowRight className="h-4 w-4 text-slate-400 opacity-0 -translate-x-2 group-hover/item:opacity-100 group-hover/item:translate-x-0 transition-all" />
                                                    </button>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-sm text-slate-500 py-4 text-center">No customer organizations found.</p>
                                        )}

                                        {hasPlatformTenant && (
                                            <>
                                                {candidateTenants.length > 0 && (
                                                    <div className="my-2 flex items-center justify-center gap-3">
                                                        <div className="h-px w-full bg-slate-200 dark:bg-white/10" />
                                                        <span className="text-xs text-slate-400 uppercase tracking-widest font-semibold">or</span>
                                                        <div className="h-px w-full bg-slate-200 dark:bg-white/10" />
                                                    </div>
                                                )}
                                                <button
                                                    onClick={() => handleLoginWithSlug('00000000-0000-0000-0000-000000000000')}
                                                    className="w-full relative group/mgmt flex items-center justify-center gap-3 bg-white dark:bg-slate-900 text-slate-900 dark:text-white border border-slate-200 dark:border-white/10 px-6 py-4 rounded-xl font-semibold transition-all duration-300 hover:bg-slate-50 dark:hover:bg-slate-800 hover:scale-[1.02] active:scale-[0.98] shadow-sm hover:shadow-md hover:border-slate-300 dark:hover:border-slate-700"
                                                >
                                                    <span className="tracking-wide text-sm">Platform Administration</span>
                                                    <ArrowRight className="w-4 h-4 opacity-70 group-hover/mgmt:translate-x-1 transition-all duration-300" />
                                                </button>
                                            </>
                                        )}

                                        <button
                                            onClick={() => setMode('discover')}
                                            className="mt-4 text-xs font-medium text-slate-500 hover:text-slate-800 dark:hover:text-slate-300 transition-colors underline underline-offset-4"
                                        >
                                            Try another email or domain
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            <style>{`
                @keyframes shimmer {
                    100% { transform: translateX(100%); }
                }
                .animate-shimmer {
                    animation: shimmer 2s infinite linear;
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(156, 163, 175, 0.3);
                    border-radius: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(156, 163, 175, 0.5);
                }
            `}</style>
        </div>
    );
};

export default LoginPage;
